// Eskom PARCC — Notifications (admin).
//
// The delivery control TK asked for (Jul 2026): every transaction update —
// allocations, revision requests, QA notes, completed reviews — is sent to every
// administrator automatically, and this screen lets an admin switch delivery on
// or off per person (including for other admins), per channel. The switches ride
// on the AccessGrant row (null = on; off only on an explicit false) and are
// WRITTEN only through the privileged admin-api, which audits every change.
// Account and security email (invites, passwords, access changes) is deliberately
// not governed here — muting someone must never hide a security-relevant notice.
import { useMemo } from 'react';
import {
  Badge,
  LoadingState,
  ErrorState,
  EmptyState,
  Pager,
  usePagination,
} from '../../components/ui';
import { useAccessGrants, useSetNotificationPrefs } from '../../hooks';
import { ROLE_LABELS } from '../../lib/permissions';
import { fullPersonName } from '../../lib/people';
import type { Role } from '../../lib/access';
import type { AccessGrantRecord } from '../../lib/db';

/** Admins first (this screen is mostly about them), then by role, then email. */
const ROLE_ORDER: readonly string[] = [
  'admin',
  'manager',
  'teamLead',
  'reviewer',
  'dataCoordinator',
];

/**
 * A channel is ON unless it is explicitly false (null = default-on). This is
 * the RENDERING twin of the server's authoritative gate — notifyChannels() in
 * admin-api/notifyRecipients.js — and must stay in step with it (the frontend
 * cannot import the admin-api module).
 */
function isOn(flag: boolean | null): boolean {
  return flag !== false;
}

export function NotificationsAdminPage(): React.JSX.Element {
  const grantsQuery = useAccessGrants();
  const setPrefs = useSetNotificationPrefs();

  const rows = useMemo(() => {
    const active = (grantsQuery.data ?? []).filter((g) => g.active);
    return [...active].sort((a, b) => {
      const r = ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role);
      return r !== 0 ? r : a.email.localeCompare(b.email);
    });
  }, [grantsQuery.data]);

  // Page the roster — both themes (Lumen 7–8/page, Legacy 50).
  const pagination = usePagination(rows);

  if (grantsQuery.isLoading) return <LoadingState message="Loading notification settings…" />;
  if (grantsQuery.error) {
    const err = grantsQuery.error;
    return (
      <ErrorState
        message="Could not load the access list."
        detail={err instanceof Error ? err.message : String(err)}
        onRetry={() => void grantsQuery.refetch()}
      />
    );
  }

  // Only the ROW being saved locks (both its switches) while its save is in
  // flight: flip() derives the full two-flag state from the cached row, so a
  // second click on the SAME row before its cache patch (useSetNotificationPrefs
  // onSuccess) would write stale values and silently undo the first change. Other
  // rows stay interactive (owner feedback Aug 2026 — the whole table used to lock).
  const savingEmail = setPrefs.isPending ? setPrefs.variables?.email : undefined;

  function flip(g: AccessGrantRecord, channel: 'inApp' | 'email'): void {
    // Always write the FULL definite state so the row never carries ambiguous
    // nulls once an admin has touched it.
    setPrefs.mutate({
      email: g.email,
      notifyInApp: channel === 'inApp' ? !isOn(g.notifyInApp) : isOn(g.notifyInApp),
      notifyEmail: channel === 'email' ? !isOn(g.notifyEmail) : isOn(g.notifyEmail),
    });
  }

  return (
    <div className="card">
      <div className="section-head">
        <div>
          <h3>Notifications</h3>
          <div className="small">
            Every transaction update — allocations, revision requests, QA notes and completed
            reviews — is sent to every administrator automatically. Use the switches to control
            delivery per person: “In-app” is the bell centre, “Email” is the branded mail (including
            the consolidated working-papers share). Account and security email — invitations,
            passwords, access and role changes — is always delivered.
          </div>
        </div>
      </div>
      {rows.length === 0 ? (
        <EmptyState message="No active users yet." />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>Division</th>
                <th>In-app</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {pagination.slice.map((g) => (
                <tr key={g.email}>
                  <td>
                    <div>{fullPersonName(g)}</div>
                    <div className="small">{g.email}</div>
                  </td>
                  <td>
                    <Badge>{ROLE_LABELS[g.role as Role] ?? g.role}</Badge>
                  </td>
                  <td>{g.division ?? '—'}</td>
                  <td>
                    <label
                      className="small"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      <input
                        type="checkbox"
                        data-testid={`notify-inapp-${g.email}`}
                        aria-label={`In-app notifications for ${g.email}`}
                        checked={isOn(g.notifyInApp)}
                        disabled={savingEmail === g.email}
                        onChange={() => flip(g, 'inApp')}
                      />
                      {isOn(g.notifyInApp) ? 'On' : 'Off'}
                    </label>
                  </td>
                  <td>
                    <label
                      className="small"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    >
                      <input
                        type="checkbox"
                        data-testid={`notify-email-${g.email}`}
                        aria-label={`Email notifications for ${g.email}`}
                        checked={isOn(g.notifyEmail)}
                        disabled={savingEmail === g.email}
                        onChange={() => flip(g, 'email')}
                      />
                      {isOn(g.notifyEmail) ? 'On' : 'Off'}
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager pagination={pagination} noun="person" />
        </div>
      )}
    </div>
  );
}
